import React, { useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DURATIONS = [12, 24, 36, 48, 60];

function formatCHF(n) {
  if (!isFinite(n)) return "—";
  return new Intl.NumberFormat("fr-CH", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.round(n));
}

export default function Simulateur() {
  const [price, setPrice] = useState(14900);
  const [apport, setApport] = useState(0);
  const [months, setMonths] = useState(36);
  const [insurance, setInsurance] = useState(true);

  // Contact mini-form
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const calc = useMemo(() => {
    const financed = Math.max(price - apport, 0);
    const baseMonthly = months > 0 ? financed / months : 0;
    // Assurance La Mobilière indicative : 0.7% / an du prix de la montre, mensualisé
    const insuranceMonthly = insurance ? (price * 0.007) / 12 : 0;
    const monthly = baseMonthly + insuranceMonthly;
    return { financed, baseMonthly, insuranceMonthly, monthly, total: financed };
  }, [price, apport, months, insurance]);

  const handleApportChange = (val) => {
    const v = Math.max(0, Math.min(Number(val) || 0, price));
    setApport(v);
  };

  const submitSimulation = async (e) => {
    e.preventDefault();
    if (!name || !email) {
      toast.error("Veuillez renseigner votre nom et email.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name,
        email,
        phone,
        message: `[Simulation paiement échelonné]
Prix du bien : CHF ${formatCHF(price)}
Apport : CHF ${formatCHF(apport)}
Montant financé : CHF ${formatCHF(calc.financed)}
Durée : ${months} mois
Assurance La Mobilière : ${insurance ? `OUI (CHF ${formatCHF(calc.insuranceMonthly)} /mois)` : "NON"}
Mensualité totale estimée : CHF ${formatCHF(calc.monthly)} / mois`,
      });
      toast.success(
        "Simulation envoyée ! Notre conseiller vous recontacte sous 24h."
      );
      setName("");
      setEmail("");
      setPhone("");
    } catch {
      toast.error("Erreur lors de l'envoi. Réessayez plus tard.");
    } finally {
      setSubmitting(false);
    }
  };

  // Slider styles
  const sliderStyle = {
    background: `linear-gradient(to right, #F06020 0%, #F06020 var(--p, 0%), rgba(0,18,38,0.15) var(--p, 0%), rgba(0,18,38,0.15) 100%)`,
  };
  const priceProgress = ((price - 1000) / (100000 - 1000)) * 100;
  const apportProgress = price > 0 ? (apport / price) * 100 : 0;

  return (
    <section
      id="simulateur"
      data-testid="simulateur-section"
      className="bg-[#FCFAF6] py-16 md:py-32 border-t border-[#001226]/10"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          {/* Left — copy */}
          <div className="lg:col-span-5 lg:sticky lg:top-32">
            <p className="text-[10px] uppercase tracking-[0.4em] text-accent mb-6">
              Simulateur
            </p>
            <h2 className="font-serif font-light text-[#001226] text-4xl sm:text-5xl lg:text-6xl leading-[1] tracking-tight">
              Calculez votre
              <br />
              <span className="italic">mensualité.</span>
            </h2>
            <p className="mt-8 text-[#1E2A40] leading-relaxed font-light max-w-md">
              Faites glisser les curseurs pour découvrir en temps réel la
              mensualité adaptée à votre projet. Avec ou sans apport,
              jusqu'à 60 mois — sans frais cachés ni surprise.
            </p>

            {/* Trust signals */}
            <div className="mt-10 space-y-4">
              {[
                "Réponse confidentielle sous 24h",
                "0% de frais cachés",
                "Sans engagement, sans dossier complexe",
                "Paiement par carte ou virement",
              ].map((t) => (
                <div key={t} className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 bg-accent rounded-full shrink-0" />
                  <span className="text-sm text-[#1E2A40]">{t}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right — calculator */}
          <div className="lg:col-span-7">
            <div className="bg-white border border-[#001226]/10 shadow-[0_30px_80px_-40px_rgba(0,18,38,0.25)]">
              {/* Calculator inputs */}
              <div className="p-6 sm:p-8 md:p-10 border-b border-[#001226]/10">
                {/* Price */}
                <div className="mb-10">
                  <div className="flex items-baseline justify-between mb-3 gap-3">
                    <label
                      htmlFor="sim-price"
                      className="text-[10px] uppercase tracking-[0.3em] text-[#5C6373]"
                    >
                      Prix de la montre
                    </label>
                    <div className="flex items-baseline gap-1 shrink-0">
                      <input
                        type="number"
                        id="sim-price-input"
                        data-testid="sim-price-input"
                        value={price}
                        min={1000}
                        max={500000}
                        step={100}
                        onChange={(e) => {
                          const v = Math.max(
                            1000,
                            Math.min(500000, Number(e.target.value) || 0)
                          );
                          setPrice(v);
                          if (apport > v) setApport(v);
                        }}
                        className="w-24 sm:w-32 text-right font-serif text-xl md:text-2xl text-[#001226] bg-transparent border-b border-[#001226]/20 focus:border-accent transition-colors"
                      />
                      <span className="text-xs md:text-sm text-[#5C6373]">CHF</span>
                    </div>
                  </div>
                  <input
                    id="sim-price"
                    data-testid="sim-price-slider"
                    type="range"
                    min={1000}
                    max={100000}
                    step={500}
                    value={Math.min(price, 100000)}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      setPrice(v);
                      if (apport > v) setApport(v);
                    }}
                    style={{ ...sliderStyle, "--p": `${priceProgress}%` }}
                    className="luxe-slider w-full"
                  />
                  <div className="mt-2 flex justify-between text-[10px] uppercase tracking-[0.25em] text-[#5C6373]">
                    <span>1'000.-</span>
                    <span>100'000.- +</span>
                  </div>
                </div>

                {/* Apport */}
                <div className="mb-10">
                  <div className="flex items-baseline justify-between mb-3 gap-3">
                    <label
                      htmlFor="sim-apport"
                      className="text-[10px] uppercase tracking-[0.3em] text-[#5C6373]"
                    >
                      Apport initial
                    </label>
                    <div className="flex items-baseline gap-1 shrink-0">
                      <input
                        type="number"
                        id="sim-apport-input"
                        data-testid="sim-apport-input"
                        value={apport}
                        min={0}
                        max={price}
                        step={100}
                        onChange={(e) => handleApportChange(e.target.value)}
                        className="w-24 sm:w-32 text-right font-serif text-xl md:text-2xl text-[#001226] bg-transparent border-b border-[#001226]/20 focus:border-accent transition-colors"
                      />
                      <span className="text-xs md:text-sm text-[#5C6373]">CHF</span>
                    </div>
                  </div>
                  <input
                    id="sim-apport"
                    data-testid="sim-apport-slider"
                    type="range"
                    min={0}
                    max={price}
                    step={100}
                    value={apport}
                    onChange={(e) => handleApportChange(e.target.value)}
                    style={{ ...sliderStyle, "--p": `${apportProgress}%` }}
                    className="luxe-slider w-full"
                  />
                  <div className="mt-2 flex justify-between text-[10px] uppercase tracking-[0.25em] text-[#5C6373]">
                    <span>Aucun apport</span>
                    <span>{formatCHF(price)} CHF</span>
                  </div>
                </div>

                {/* Months */}
                <div>
                  <label className="text-[10px] uppercase tracking-[0.3em] text-[#5C6373] block mb-4">
                    Durée du paiement
                  </label>
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                    {DURATIONS.map((d) => (
                      <button
                        key={d}
                        type="button"
                        onClick={() => setMonths(d)}
                        data-testid={`sim-months-${d}`}
                        className={`py-3 text-[10px] md:text-[11px] uppercase tracking-[0.2em] md:tracking-[0.25em] border transition-colors ${
                          months === d
                            ? "bg-accent text-white border-accent"
                            : "bg-transparent text-[#001226] border-[#001226]/20 hover:border-accent hover:text-accent"
                        }`}
                      >
                        {d} mois
                      </button>
                    ))}
                  </div>
                </div>

                {/* Insurance toggle */}
                <div className="mt-10 pt-8 border-t border-[#001226]/10">
                  <label
                    htmlFor="sim-insurance"
                    className="flex items-start gap-4 cursor-pointer group"
                  >
                    <div className="relative shrink-0 mt-1">
                      <input
                        id="sim-insurance"
                        data-testid="sim-insurance-toggle"
                        type="checkbox"
                        checked={insurance}
                        onChange={(e) => setInsurance(e.target.checked)}
                        className="peer sr-only"
                      />
                      <div className="w-11 h-6 bg-[#001226]/15 peer-checked:bg-accent transition-colors" />
                      <div className="absolute top-0.5 left-0.5 w-5 h-5 bg-white shadow transition-transform peer-checked:translate-x-5" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-[#001226] font-medium text-base">
                          Assurance vol & casse
                        </span>
                        <span className="text-[9px] uppercase tracking-[0.25em] text-[#5C6373]">
                          via
                        </span>
                        <img
                          src="/brands/mobiliere.png"
                          alt="La Mobilière"
                          className="h-5 md:h-7 w-auto"
                          draggable={false}
                        />
                      </div>
                      <p className="mt-2 text-sm text-[#1E2A40] font-light leading-relaxed">
                        Couverture vol, perte, casse et dommages accidentels par
                        notre partenaire <strong>La Mobilière</strong> — la plus
                        ancienne compagnie d'assurance privée de Suisse.
                      </p>
                      <p className="mt-2 text-xs text-[#5C6373]">
                        + CHF {formatCHF(calc.insuranceMonthly)} / mois
                        {" "}<span className="italic">(à titre indicatif)</span>
                      </p>
                    </div>
                  </label>
                </div>
              </div>

              {/* Result */}
              <div className="bg-[#001226] text-white p-6 sm:p-8 md:p-10">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-6 sm:gap-8 mb-8">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-400 mb-2">
                      Montant financé
                    </p>
                    <p
                      data-testid="sim-financed"
                      className="font-serif text-3xl text-white font-light"
                    >
                      {formatCHF(calc.financed)}
                      <span className="text-sm text-zinc-400 ml-1">CHF</span>
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-400 mb-2">
                      Durée
                    </p>
                    <p className="font-serif text-3xl text-white font-light">
                      {months}
                      <span className="text-sm text-zinc-400 ml-1">mois</span>
                    </p>
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <p className="text-[10px] uppercase tracking-[0.3em] text-accent mb-2">
                      Votre mensualité
                    </p>
                    <p
                      data-testid="sim-monthly"
                      className="font-serif text-4xl md:text-5xl text-accent font-light leading-none"
                    >
                      {formatCHF(calc.monthly)}
                      <span className="text-sm text-zinc-400 ml-1">/mois</span>
                    </p>
                    {insurance && (
                      <p className="mt-2 text-xs text-zinc-400">
                        dont CHF {formatCHF(calc.insuranceMonthly)} d'assurance
                      </p>
                    )}
                  </div>
                </div>

                {/* Contact form */}
                <form
                  onSubmit={submitSimulation}
                  data-testid="simulateur-form"
                  className="border-t border-white/10 pt-8"
                >
                  <p className="text-xs uppercase tracking-[0.3em] text-zinc-400 mb-5">
                    Recevoir cette simulation par email
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <input
                      data-testid="sim-name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Nom complet *"
                      className="bg-transparent border-b border-white/30 px-0 py-3 text-white text-sm focus:border-accent transition-colors placeholder:text-zinc-400"
                      required
                    />
                    <input
                      data-testid="sim-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Email *"
                      className="bg-transparent border-b border-white/30 px-0 py-3 text-white text-sm focus:border-accent transition-colors placeholder:text-zinc-400"
                      required
                    />
                    <input
                      data-testid="sim-phone"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="Téléphone"
                      className="bg-transparent border-b border-white/30 px-0 py-3 text-white text-sm focus:border-accent transition-colors placeholder:text-zinc-400"
                    />
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <p className="text-xs text-zinc-400 max-w-md">
                      * Simulation indicative. Conditions définitives
                      communiquées par notre conseiller après étude de votre
                      dossier.
                    </p>
                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid="sim-submit"
                      className="inline-flex items-center gap-3 px-8 py-4 bg-accent text-white text-[11px] uppercase tracking-[0.25em] hover:bg-accent-dark transition-colors disabled:opacity-50"
                    >
                      {submitting ? "Envoi…" : "Envoyer ma simulation"}
                      <span aria-hidden>→</span>
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
